import React from 'react';
import { Tr, Td, Text, HStack, Icon, Skeleton } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';

import type {
  AvailableCarrier,
  CarrierQuote,
  CarriersVehicle,
} from 'api/quote-marketplace';
import type { QuoteMarketplaceTheme } from '../../hooks';
import {
  CarrierCell,
  STICKY_LABEL_WIDTH,
  STICKY_REQUESTED_WIDTH,
} from './CarrierCell';
import { CoverageMatchIndicator } from './CoverageMatchIndicator';
import {
  isCoverageAvailable,
  hasAccidentBenefits,
} from '../../constants/provinceCoverageAvailability';
import { getMatchStatus } from '../../utils/coverageMatchStatus';
import {
  AUTO_COVERAGE_SECTIONS,
  LIABILITY_ROW,
  type AutoCoverageRowConfig,
  type AutoCoverageSectionConfig,
  type AutoSectionKey,
} from '../../constants/autoCarrierCoverages';

type GetLabelFn = (value: string, field: any) => string;

interface VehicleCoveragesSectionProps {
  vehicle: CarriersVehicle;
  vehicleIndex: number;
  availableCarriers: AvailableCarrier[];
  carrierQuotes: CarrierQuote[];
  selectedCarrierQuoteId: number | null;
  expandedSections: AutoSectionKey[];
  isLoading: boolean;
  theme: QuoteMarketplaceTheme;
  getLabel: GetLabelFn;
  onToggleSection: (section: AutoSectionKey) => void;
  provinceCode?: number;
}

export const VehicleCoveragesSection = ({
  vehicle,
  vehicleIndex,
  availableCarriers,
  carrierQuotes,
  selectedCarrierQuoteId,
  expandedSections,
  isLoading,
  theme,
  getLabel,
  onToggleSection,
  provinceCode,
}: VehicleCoveragesSectionProps) => {
  const coverages = vehicle.coverages;

  const showAccidentBenefits = provinceCode
    ? hasAccidentBenefits(provinceCode)
    : true;

  const filteredSections = AUTO_COVERAGE_SECTIONS.filter((section) => {
    if (section.key === 'standardAB' || section.key === 'optionalAB') {
      return showAccidentBenefits;
    }
    return true;
  });

  const getFilteredRows = (rows: AutoCoverageRowConfig[]) => {
    if (!provinceCode) return rows;
    return rows.filter((row) => {
      if (!row.coverageKey) return true;
      return isCoverageAvailable(row.coverageKey, provinceCode);
    });
  };

  const getQuoteForCarrier = (
    carrier: AvailableCarrier,
  ): CarrierQuote | undefined => {
    return carrierQuotes.find((q) => q.acturis_product === carrier.product);
  };

  const isCarrierSelected = (carrier: AvailableCarrier): boolean => {
    const quote = getQuoteForCarrier(carrier);
    return quote?.id === selectedCarrierQuoteId;
  };

  const getRatedCoverageForVehicle = (carrier: AvailableCarrier) => {
    const quote = getQuoteForCarrier(carrier);
    if (!quote?.rated_coverages?.vehicles) return null;
    return quote.rated_coverages.vehicles[vehicleIndex] || null;
  };

  const renderRatedValueWithIndicator = (
    carrier: AvailableCarrier,
    row: AutoCoverageRowConfig,
  ) => {
    const quote = getQuoteForCarrier(carrier);

    if ((!quote || quote.status === 'pending') && isLoading) {
      return <Skeleton height="16px" width="50px" mx="auto" />;
    }

    if (!quote || quote.status === 'pending') {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    if (
      quote.status === 'declined' ||
      quote.status === 'error' ||
      quote.status === 'timed_out'
    ) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    const ratedCoverages = getRatedCoverageForVehicle(carrier);
    const isRequested = row.isRequested
      ? row.isRequested(coverages, vehicle)
      : true;

    const rawRequested = row.getRawRequestedValue
      ? row.getRawRequestedValue(coverages, vehicle, getLabel)
      : null;
    const rawRated =
      row.getRawRatedValue && ratedCoverages
        ? row.getRawRatedValue(ratedCoverages)
        : null;

    const status = getMatchStatus(rawRequested, rawRated, isRequested);

    const ratedValue =
      row.getRatedValue && ratedCoverages
        ? row.getRatedValue(ratedCoverages, getLabel)
        : undefined;

    const displayValue = ratedValue
      ? row.codeField
        ? getLabel(String(ratedValue), row.codeField) || String(ratedValue)
        : String(ratedValue)
      : null;

    return <CoverageMatchIndicator status={status} value={displayValue} />;
  };

  const renderRatedValue = (
    carrier: AvailableCarrier,
    getValue:
      | ((coverages: any, getLabel?: GetLabelFn) => string | undefined)
      | undefined,
    codeField?: string,
  ) => {
    const quote = getQuoteForCarrier(carrier);

    if ((!quote || quote.status === 'pending') && isLoading) {
      return <Skeleton height="16px" width="50px" mx="auto" />;
    }

    if (!quote || quote.status === 'pending') {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    if (
      quote.status === 'declined' ||
      quote.status === 'error' ||
      quote.status === 'timed_out'
    ) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    if (!getValue) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    const ratedCoverages = getRatedCoverageForVehicle(carrier);
    const ratedValue = ratedCoverages
      ? getValue(ratedCoverages, getLabel)
      : undefined;

    if (!ratedValue) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    const displayValue = codeField
      ? getLabel(String(ratedValue), codeField) || String(ratedValue)
      : String(ratedValue);

    return (
      <Text fontSize="sm" fontWeight="500">
        {displayValue}
      </Text>
    );
  };

  const renderCarrierCellContent = (
    hasValue: boolean,
    carrier: AvailableCarrier,
  ) => {
    const quote = getQuoteForCarrier(carrier);

    if ((!quote || quote.status === 'pending') && isLoading) {
      return <Skeleton height="16px" width="50px" mx="auto" />;
    }

    if (
      !quote ||
      quote.status === 'pending' ||
      quote.status === 'declined' ||
      quote.status === 'error' ||
      quote.status === 'timed_out' ||
      !hasValue
    ) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          {quote?.status === 'declined' ||
          quote?.status === 'error' ||
          quote?.status === 'timed_out' ||
          !hasValue
            ? 'No'
            : '-'}
        </Text>
      );
    }

    return (
      <Text color={theme.labelColor} fontSize="sm">
        -
      </Text>
    );
  };

  const renderLiabilityRow = () => {
    const requestedValue = LIABILITY_ROW.getRequestedValue(
      coverages,
      vehicle,
      getLabel,
    );

    return (
      <Tr _hover={{ bg: theme.hoverBg }}>
        <Td
          position="sticky"
          left={0}
          bg={theme.bgColor}
          borderColor={theme.borderColor}
          py={3}
          pl={8}
          zIndex={2}
        >
          <Text fontSize="sm">Liability</Text>
        </Td>
        <Td
          bg={theme.requestedBg}
          borderColor={theme.borderColor}
          py={3}
          textAlign="center"
          position="sticky"
          left={`${STICKY_LABEL_WIDTH}px`}
          zIndex={2}
          minW={`${STICKY_REQUESTED_WIDTH}px`}
        >
          <Text fontSize="sm" fontWeight="500">
            {requestedValue}
          </Text>
        </Td>
        {availableCarriers.map((carrier, index) => {
          const isSelected = isCarrierSelected(carrier);
          return (
            <CarrierCell
              key={carrier.carrier_id}
              isSelected={isSelected}
              theme={theme}
              isSticky={index === 0 && isSelected}
            >
              {renderRatedValueWithIndicator(carrier, LIABILITY_ROW)}
            </CarrierCell>
          );
        })}
      </Tr>
    );
  };

  const renderSectionHeader = (section: AutoCoverageSectionConfig) => {
    const isExpanded = expandedSections.includes(section.key);
    return (
      <Tr
        onClick={() => onToggleSection(section.key)}
        cursor="pointer"
        _hover={{ bg: theme.hoverBg }}
        bg={theme.sectionHeaderBg}
      >
        <Td
          position="sticky"
          left={0}
          bg={theme.sectionHeaderBg}
          borderColor={theme.borderColor}
          py={3}
          zIndex={2}
        >
          <HStack spacing={2}>
            <Icon
              as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
              boxSize={4}
              color={theme.sectionHeaderColor}
            />
            <Text
              fontSize="sm"
              fontWeight="500"
              color={theme.sectionHeaderColor}
            >
              {section.label}
            </Text>
          </HStack>
        </Td>
        <Td
          bg={theme.requestedBg}
          borderColor={theme.borderColor}
          py={3}
          position="sticky"
          left={`${STICKY_LABEL_WIDTH}px`}
          zIndex={2}
          minW={`${STICKY_REQUESTED_WIDTH}px`}
        />
        {availableCarriers.map((carrier, index) => {
          const isSelected = isCarrierSelected(carrier);
          return (
            <CarrierCell
              key={carrier.carrier_id}
              isSelected={isSelected}
              theme={theme}
              isSticky={index === 0 && isSelected}
            />
          );
        })}
      </Tr>
    );
  };

  const renderCoverageRow = (row: AutoCoverageRowConfig) => {
    const requestedValue = row.getRequestedValue(coverages, vehicle, getLabel);
    const showInCarrierCells = row.showInCarrierCells
      ? row.showInCarrierCells(coverages, vehicle)
      : true;
    const hasIndicatorSupport =
      row.getRawRequestedValue && row.getRawRatedValue && row.isRequested;

    return (
      <Tr key={row.label} _hover={{ bg: theme.hoverBg }}>
        <Td
          position="sticky"
          left={0}
          bg={theme.bgColor}
          borderColor={theme.borderColor}
          py={3}
          pl={12}
          zIndex={2}
        >
          <Text fontSize="sm">{row.label}</Text>
        </Td>
        <Td
          bg={theme.requestedBg}
          borderColor={theme.borderColor}
          py={3}
          textAlign="center"
          position="sticky"
          left={`${STICKY_LABEL_WIDTH}px`}
          zIndex={2}
          minW={`${STICKY_REQUESTED_WIDTH}px`}
        >
          <Text fontSize="sm" fontWeight="500">
            {requestedValue}
          </Text>
        </Td>
        {availableCarriers.map((carrier, index) => {
          const isSelected = isCarrierSelected(carrier);
          return (
            <CarrierCell
              key={carrier.carrier_id}
              isSelected={isSelected}
              theme={theme}
              isSticky={index === 0 && isSelected}
            >
              {hasIndicatorSupport
                ? renderRatedValueWithIndicator(carrier, row)
                : row.getRatedValue
                  ? renderRatedValue(carrier, row.getRatedValue, row.codeField)
                  : renderCarrierCellContent(showInCarrierCells, carrier)}
            </CarrierCell>
          );
        })}
      </Tr>
    );
  };

  return (
    <>
      {renderLiabilityRow()}
      {filteredSections.map((section) => {
        const filteredRows = getFilteredRows(section.rows);
        if (filteredRows.length === 0) return null;
        return (
          <React.Fragment key={section.key}>
            {renderSectionHeader(section)}
            {expandedSections.includes(section.key) &&
              filteredRows.map((row) => renderCoverageRow(row))}
          </React.Fragment>
        );
      })}
    </>
  );
};

export type { AutoSectionKey as SectionKey };
